import { Linkedin, Github } from "lucide-react";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.82l-5.34-6.96L4.2 22H.94l8.02-9.16L1.5 2h6.96l4.82 6.38L18.244 2Zm-2.39 18.2h1.81L7.23 3.72H5.3l10.553 16.48Z" />
  </svg>
);

const PORTRAIT =
  "https://unitedtechlab.com/_next/image?url=%2Fkuldeep.jpg&w=3840&q=75";

export default function Founder() {
  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Kuldeep Singh Jangir",
    jobTitle: "Founder",
    worksFor: { "@type": "Organization", name: "United Techlab" },
    email: "kuldeep@unitedtechlab.com",
    image: PORTRAIT,
    url: "https://unitedtechlab.com/founder",
    sameAs: [
      "https://www.linkedin.com/in/jspeedluk/",
      "https://x.com/jspeedluk",
      "https://github.com/jspeedluk",
    ],
  };
  return (
    <>
      <SEO
        title="Founder | Kuldeep Singh Jangir"
        description="Kuldeep Singh Jangir, Founder of United Techlab. Ex-Goldman Sachs engineering. Building a verification-first engineering firm for the enterprise."
        path="/founder"
        image={PORTRAIT}
        type="profile"
        jsonLd={personJsonLd}
      />
      <section className="relative border-b border-white/10 bg-obsidian">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="relative border border-white/10 bg-carbon p-2">
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={PORTRAIT}
                  alt="Kuldeep Singh Jangir — Founder, United Techlab"
                  className="absolute inset-0 h-full w-full object-cover grayscale contrast-[1.05]"
                  data-testid="founder-portrait"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
              </div>
              <div className="mt-3 px-2 py-1 flex items-center justify-between font-mono text-[11px] text-white/60">
                <span>file: /founder/kuldeep.jpg</span>
                <span className="text-neon-green">● live</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="chip text-neon-green">&gt; /founder</div>
            <h1 className="mt-3 font-display font-black text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter leading-[0.95]">
              Kuldeep Singh Jangir
            </h1>
            <p className="mt-3 font-mono text-neon-cyan text-sm uppercase tracking-[0.2em]">
              Founder · United Techlab
            </p>

            <div className="mt-8 space-y-5 text-white/80 leading-relaxed text-[15px]">
              <p>
                I built United Techlab after spending years inside complex
                enterprise systems where legacy code wasn't just old, it was
                the operational backbone of the business.
              </p>
              <p>
                During my time working with major financial institutions like{" "}
                <span className="text-neon-green">Goldman Sachs</span>, a
                significant part of my work involved migrating legacy systems to
                modern technology stacks. The hardest part wasn't rewriting
                code. The hardest part was{" "}
                <em className="text-white">
                  verifying that the new system behaves exactly like the old
                  one.
                </em>
              </p>
              <p>
                Modern AI tools are powerful, but on their own, they are
                dangerous. Without a verification-first framework, you're just
                generating buggy code faster. Enterprises need{" "}
                <span className="text-neon-cyan">risk mitigation</span>, a way
                to mathematically prove that the new system does what the old
                system did.
              </p>
              <p>
                I founded United Techlab to bring engineering rigor back to
                modernization, and to extend it into the rest of the IT stack
                enterprises rely on: development, cloud, AI engineering.
              </p>
            </div>

            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="chip text-white/50 mb-4">// contact_the_founder</div>
              <div className="flex flex-wrap gap-3">
                <a
                  data-testid="contact-email-link"
                  href="mailto:kuldeep@unitedtechlab.com"
                  className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-sm text-white hover:border-neon-green hover:text-neon-green transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  kuldeep@unitedtechlab.com
                </a>
                <a
                  href="https://www.linkedin.com/in/jspeedluk/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="founder-linkedin"
                  className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-sm text-white hover:border-neon-green hover:text-neon-green transition-colors"
                >
                  <Linkedin size={16} /> linkedin.com/in/jspeedluk
                </a>
                <a
                  href="https://x.com/jspeedluk"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="founder-x"
                  className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-sm text-white hover:border-neon-green hover:text-neon-green transition-colors"
                >
                  <XIcon /> @jspeedluk
                </a>
                <a
                  href="https://github.com/jspeedluk"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="founder-github"
                  className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-sm text-white hover:border-neon-green hover:text-neon-green transition-colors"
                >
                  <Github size={16} /> github.com/jspeedluk
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="> /work_with_kuldeep"
        title="Start a conversation."
        subtitle="Tell Kuldeep what you're working on. Every enquiry is read by the founder first."
        ctaLabel="Contact Kuldeep"
        secondaryLabel="Read about the company"
        secondaryTo="/about"
      />
    </>
  );
}
